import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, FileText, Gauge, LayoutDashboard, RefreshCw, Truck, Warehouse, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Loader } from "@/components/ui/Loader";
import { useLocacoesAutoRefresh } from "@/hooks/useLocacoesAutoRefresh";
import { useAppAccess } from "@/hooks/useAppAccess";
import {
  cancelarLocacao,
  concluirManutencao,
  createAbastecimento,
  createContrato,
  createManutencao,
  createMaquinario,
  createOperador,
  createRegistroHorimetro,
  deleteContrato,
  deleteMaquinario,
  deleteOperador,
  deleteRegistroHorimetro,
  deleteAbastecimento,
  encerrarLocacao,
  fetchLocacoesSnapshot,
  fetchLocacoesUsuarios,
  extractApiError,
  isCombustivelPagoCliente,
  updateAbastecimento,
  updateContrato,
  updateContratoPipelineFase,
  aprovarRegistroHorimetro,
  rejeitarRegistroHorimetro,
  aprovarManutencao,
  rejeitarManutencao,
  updateRegistroHorimetro,
  updateManutencaoStatus,
  updateManutencao,
  deleteManutencao,
  updateMaquinario,
  updateOperador,
  type AbastecimentoDto,
  type ContratoDto,
  type ContratoFormValues,
  type ContratoOperadorDto,
  type ContratoPipelineFase,
  type FilaAprovacaoItem,
  type FiltroTipoAtivo,
  type AtivoTipo,
  resolveAtivoTipo,
  type LocacaoDto,
  type MaquinarioDto,
  type MaquinarioFormValues,
  type ManutencaoDto,
  type ManutencaoStatus,
  type OperadorCatalogDto,
  type OperadorFormValues,
  type PeriodRange,
  type RegistroHorimetroDto,
  type LocacoesScope,
} from "@/services/locacoes";
import { LocacoesDashboard } from "./LocacoesDashboard";
import { EMPTY_RESUMO_FILTERS, LocacoesResumoDashboard, type ResumoDashboardFilters } from "./LocacoesResumoDashboard";
import { LocacoesFilterBar } from "./LocacoesFilterBar";
import { EMPTY_LOCACOES_SCOPE, normalizeLocacoesScope, type LocacoesScopeValue } from "./LocacoesScopeFilter";
import { ModalMaquinario } from "./ModalMaquinario";
import { ModalOperador } from "./ModalOperador";
import { ModalContrato } from "./ModalContrato";
import { ContratoDetailView } from "./ContratoDetailView";
import { TabContratos } from "./TabContratos";
import { ModalContratosArquivados } from "./ModalContratosArquivados";
import { TabCadastroMaquinas } from "./TabCadastroMaquinas";
import { TabCadastroEquipamentos } from "./TabCadastroEquipamentos";
import { TabCadastroOperadores } from "./TabCadastroOperadores";
import { CadastrosTabMenu, type CadastroSubTab } from "./CadastrosTabMenu";
import { TabGaragem } from "./TabGaragem";
import { TabCarradas } from "./TabCarradas";
import { GaragemMaquinaDetail } from "./GaragemMaquinaDetail";
import { registrarManutencaoGaragem } from "./garagemManutencao";
import { ModalEncerrarLocacao } from "./ModalEncerrarLocacao";
import { ModalCancelarLocacao } from "./ModalCancelarLocacao";
import { ModalManutencao } from "./ModalManutencao";
import { ModalRegistroHorimetro } from "./ModalRegistroHorimetro";
import { ModalAbastecimento } from "./ModalAbastecimento";
import { MaquinaHistoricoModal } from "./MaquinaHistoricoModal";
import { defaultLocacoesPeriod, emptyMetrics, filterContratosByTipo, filterMaquinariosByIds, filterMaquinariosByTipo, filterRegistrosDoContrato, locacoesTabClass, resolveMaquinariosAbastecimentoModal, weekPeriod } from "./locacoesUtils";

type TabId =
  | "cadastro-maquinas"
  | "cadastro-equipamentos"
  | "cadastro-operadores"
  | "resumo"
  | "dashboard"
  | "contratos"
  | "garagem"
  | "carradas";

const TABS: Array<{ id: Exclude<TabId, "cadastro-maquinas" | "cadastro-equipamentos" | "cadastro-operadores">; label: string; icon: LucideIcon }> = [
  { id: "resumo", label: "Dashboard", icon: LayoutDashboard },
  { id: "dashboard", label: "Visão Geral", icon: Gauge },
  { id: "contratos", label: "Contratos", icon: FileText },
  { id: "garagem", label: "Garagem", icon: Warehouse },
  { id: "carradas", label: "Apropriação", icon: Truck },
];

function isCadastroTab(tab: TabId): tab is CadastroSubTab {
  return tab === "cadastro-maquinas" || tab === "cadastro-equipamentos" || tab === "cadastro-operadores";
}

const EMPTY_DELTAS = { horasTrabalhadas: 0, abastecimentos: 0, litrosAbastecidos: 0, consumoMedio: 0 };

interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  action: () => void | Promise<void>;
}

function hasActiveScope(scope: LocacoesScope | LocacoesScopeValue): boolean {
  return Boolean(scope.clienteSiengeId || scope.contratoId || scope.maquinaId);
}

function resumoSnapshotMatches(
  snapshot: { period: PeriodRange; scope?: LocacoesScope | null },
  period: PeriodRange,
  filters: ResumoDashboardFilters,
): boolean {
  if (snapshot.period.startDate !== period.startDate || snapshot.period.endDate !== period.endDate) {
    return false;
  }
  const scope = snapshot.scope;
  return (
    (scope?.maquinaId || "") === filters.maquinaId &&
    (scope?.clienteSiengeId || "") === filters.clienteId &&
    (scope?.contratoId || "") === filters.contratoId &&
    !scope?.tipoContrato &&
    !scope?.contratoStatus
  );
}

export function LocacoesPage() {
  const { isAdmin } = useAppAccess();
  const [tab, setTab] = useState<TabId>("resumo");
  const [resumoPeriod, setResumoPeriod] = useState<PeriodRange>(() => weekPeriod(0));
  const [resumoFilters, setResumoFilters] = useState<ResumoDashboardFilters>(EMPTY_RESUMO_FILTERS);
  const resumoScope = useMemo<LocacoesScope>(
    () => ({
      ...(resumoFilters.clienteId ? { clienteSiengeId: resumoFilters.clienteId } : {}),
      ...(resumoFilters.contratoId ? { contratoId: resumoFilters.contratoId } : {}),
      ...(resumoFilters.maquinaId ? { maquinaId: resumoFilters.maquinaId } : {}),
    }),
    [resumoFilters],
  );
  const loadSeq = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dashboardPeriodDraft, setDashboardPeriodDraft] = useState<PeriodRange>(defaultLocacoesPeriod);
  const [dashboardPeriod, setDashboardPeriod] = useState<PeriodRange>(defaultLocacoesPeriod);
  const [scopeDraft, setScopeDraft] = useState<LocacoesScopeValue>(EMPTY_LOCACOES_SCOPE);
  const [scope, setScope] = useState<LocacoesScope>({});
  const [scopeLabel, setScopeLabel] = useState<string | null>(null);
  const [filtroTipoDraft, setFiltroTipoDraft] = useState<FiltroTipoAtivo>("todos");
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipoAtivo>("todos");
  const [chartsFiltersApplied, setChartsFiltersApplied] = useState(false);
  const [snapshot, setSnapshot] = useState<Awaited<ReturnType<typeof fetchLocacoesSnapshot>> | null>(null);
  const [usuarios, setUsuarios] = useState<Array<{ id: string; displayName: string }>>([]);

  const [maquinaModalOpen, setMaquinaModalOpen] = useState(false);
  const [maquinaModalLockedTipo, setMaquinaModalLockedTipo] = useState<AtivoTipo | undefined>(undefined);
  const [editingMaquina, setEditingMaquina] = useState<MaquinarioDto | null>(null);
  const [prefillMaquina, setPrefillMaquina] = useState<MaquinarioDto | null>(null);
  const [operadorModalOpen, setOperadorModalOpen] = useState(false);
  const [editingOperador, setEditingOperador] = useState<OperadorCatalogDto | null>(null);
  const [contratoModalOpen, setContratoModalOpen] = useState(false);
  const [contratoModalInitialTipo, setContratoModalInitialTipo] = useState<AtivoTipo>("maquinario");
  const [editingContrato, setEditingContrato] = useState<ContratoDto | null>(null);
  const [viewingContrato, setViewingContrato] = useState<ContratoDto | null>(null);
  const [manutencaoModalOpen, setManutencaoModalOpen] = useState(false);
  const [editingManutencao, setEditingManutencao] = useState<ManutencaoDto | null>(null);
  const [horimetroModalOpen, setHorimetroModalOpen] = useState(false);
  const [editingHorimetro, setEditingHorimetro] = useState<RegistroHorimetroDto | null>(null);
  const [abastecimentoModalOpen, setAbastecimentoModalOpen] = useState(false);
  const [editingAbastecimento, setEditingAbastecimento] = useState<AbastecimentoDto | null>(null);
  const [timelineMaquina, setTimelineMaquina] = useState<MaquinarioDto | null>(null);
  const [encerrarLocacaoTarget, setEncerrarLocacaoTarget] = useState<LocacaoDto | null>(null);
  const [encerrarLocacaoOpen, setEncerrarLocacaoOpen] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [cancelLocacaoTarget, setCancelLocacaoTarget] = useState<LocacaoDto | null>(null);
  const [cancelLocacaoOpen, setCancelLocacaoOpen] = useState(false);
  const [garagemMaquinaId, setGaragemMaquinaId] = useState<string | null>(null);
  const [abastecimentoPresetMaquinaId, setAbastecimentoPresetMaquinaId] = useState<string | null>(null);
  const [manutencaoGaragemMode, setManutencaoGaragemMode] = useState(false);
  const [isArquivadosModalOpen, setIsArquivadosModalOpen] = useState(false);

  const load = useCallback(
    async (opts?: {
      silent?: boolean;
      periodOverride?: PeriodRange;
      scopeOverride?: LocacoesScope;
      reportError?: boolean;
    }) => {
      const p =
        opts?.periodOverride ??
        (tab === "resumo" ? resumoPeriod : tab === "dashboard" ? dashboardPeriod : undefined);
      const s = opts?.scopeOverride ?? (tab === "resumo" ? resumoScope : scope);
      const silent = opts?.silent ?? false;
      const reportError = opts?.reportError ?? !silent;
      const seq = ++loadSeq.current;
      if (!silent) setLoading(true);
      if (reportError) setError(null);
      try {
        const data = await fetchLocacoesSnapshot(p, s);
        if (seq !== loadSeq.current) return;
        setSnapshot(data);
        setScopeLabel(data.scopeLabel ?? null);
      } catch (e) {
        if (seq !== loadSeq.current) return;
        if (reportError) setError(extractApiError(e, "Não foi possível carregar Semog Locações."));
        throw e;
      } finally {
        if (seq === loadSeq.current && !silent) setLoading(false);
      }
    },
    [dashboardPeriod, resumoPeriod, resumoScope, scope, tab],
  );

  const reloadAfterMutation = useCallback(async () => {
    await load({ silent: true, reportError: true });
  }, [load]);

  const cadastroModalAberto =
    maquinaModalOpen ||
    operadorModalOpen ||
    contratoModalOpen ||
    manutencaoModalOpen ||
    horimetroModalOpen ||
    abastecimentoModalOpen ||
    encerrarLocacaoOpen ||
    cancelLocacaoOpen;

  useLocacoesAutoRefresh(
    load,
    cadastroModalAberto || (tab === "dashboard" && !chartsFiltersApplied),
  );

  useEffect(() => {
    void load();
    fetchLocacoesUsuarios()
      .then(setUsuarios)
      .catch(() => setUsuarios([]));
  }, [load]);

  const maquinasFrota = useMemo(() => snapshot?.maquinarios ?? [], [snapshot]);
  const maquinasParaEscopo = useMemo(
    () => filterMaquinariosByTipo(maquinasFrota, filtroTipoDraft),
    [maquinasFrota, filtroTipoDraft],
  );
  const contratosParaEscopo = useMemo(
    () => filterContratosByTipo(snapshot?.contratos ?? [], maquinasFrota, filtroTipoDraft),
    [snapshot?.contratos, maquinasFrota, filtroTipoDraft],
  );

  const applyFilters = () => {
    const normalizedDraft = normalizeLocacoesScope(contratosParaEscopo, scopeDraft);
    if (!hasActiveScope(normalizedDraft)) {
      setError("Selecione cliente, contrato ou máquina/equipamento e clique em Aplicar para carregar os gráficos.");
      return;
    }
    const nextScope: LocacoesScope = {
      ...(normalizedDraft.clienteSiengeId ? { clienteSiengeId: normalizedDraft.clienteSiengeId } : {}),
      ...(normalizedDraft.contratoId ? { contratoId: normalizedDraft.contratoId } : {}),
      ...(normalizedDraft.maquinaId ? { maquinaId: normalizedDraft.maquinaId } : {}),
      ...(filtroTipoDraft !== "todos" ? { tipoContrato: filtroTipoDraft } : {}),
    };
    setScopeDraft(normalizedDraft);
    setError(null);
    setDashboardPeriod(dashboardPeriodDraft);
    setScope(nextScope);
    setFiltroTipo(filtroTipoDraft);
    setChartsFiltersApplied(true);
    void load({ periodOverride: dashboardPeriodDraft, scopeOverride: nextScope });
  };

  const openContratoView = useCallback(
    (c: ContratoDto) => {
      setViewingContrato(c);
      setError(null);
    },
    [],
  );

  const clearScope = () => {
    setScopeDraft(EMPTY_LOCACOES_SCOPE);
    setScope({});
    setChartsFiltersApplied(false);
    setScopeLabel(null);
    setError(null);
    void load({ scopeOverride: {} });
  };

  const handleScopeDraftChange = (next: LocacoesScopeValue) => {
    setScopeDraft(normalizeLocacoesScope(contratosParaEscopo, next));
  };

  const handleFiltroTipoDraftChange = (next: FiltroTipoAtivo) => {
    setFiltroTipoDraft(next);
    setScopeDraft(EMPTY_LOCACOES_SCOPE);
  };

  const hasScopeDraft = hasActiveScope(scopeDraft);
  const showDashboardCharts = chartsFiltersApplied && hasActiveScope(scope);

  const maquinasFrotaFiltradas = useMemo(
    () => filterMaquinariosByTipo(maquinasFrota, filtroTipo),
    [maquinasFrota, filtroTipo],
  );
  const contratosFiltrados = useMemo(
    () => filterContratosByTipo(snapshot?.contratos ?? [], maquinasFrota, filtroTipo),
    [snapshot?.contratos, maquinasFrota, filtroTipo],
  );
  const contratosAtivos = useMemo(() => {
    let list = contratosFiltrados;
    if (scope.clienteSiengeId) list = list.filter((c) => c.clienteSiengeId === scope.clienteSiengeId);
    if (scope.contratoId) return list.filter((c) => c.id === scope.contratoId);
    return list.filter((c) => c.status === "ativo");
  }, [contratosFiltrados, scope]);
  const manutencoesAbertas = useMemo(
    () =>
      (snapshot?.manutencoes ?? []).filter(
        (m) => m.status !== "finalizada" && m.statusAprovacao !== "pendente",
      ),
    [snapshot],
  );

  const metrics = snapshot?.metrics ?? emptyMetrics();
  const deltas = snapshot?.deltas ?? EMPTY_DELTAS;

  const viewingContratoLive = useMemo(() => {
    if (!viewingContrato || !snapshot) return null;
    return snapshot.contratos?.find((c) => c.id === viewingContrato.id) ?? null;
  }, [viewingContrato, snapshot]);

  const maquinasDashboard = useMemo(
    () => filterMaquinariosByTipo(snapshot?.maquinariosScoped ?? snapshot?.maquinarios ?? [], filtroTipo),
    [snapshot, filtroTipo],
  );

  const maquinasCadastro = useMemo(
    () => filterMaquinariosByTipo(maquinasFrota, "maquinario"),
    [maquinasFrota],
  );
  const equipamentosCadastro = useMemo(
    () => filterMaquinariosByTipo(maquinasFrota, "equipamento"),
    [maquinasFrota],
  );

  const maquinasContexto = useMemo(() => {
    if (!snapshot) return [];
    if (viewingContratoLive) return filterMaquinariosByIds(maquinasFrota, viewingContratoLive.maquinaIds);
    return maquinasDashboard;
  }, [snapshot, viewingContratoLive, maquinasFrota, maquinasDashboard]);

  const maquinariosAbastecimentoModal = useMemo(
    () =>
      resolveMaquinariosAbastecimentoModal(maquinasFrota, {
        contrato: viewingContratoLive,
        presetMaquinaId: abastecimentoPresetMaquinaId,
        editingMaquinaId: editingAbastecimento?.maquinaId ?? null,
        fallback: maquinasContexto,
      }),
    [maquinasFrota, viewingContratoLive, abastecimentoPresetMaquinaId, editingAbastecimento?.maquinaId, maquinasContexto],
  );

  const horimetroContratoContext = useMemo(() => {
    if (!viewingContratoLive || !snapshot) return null;
    return {
      id: viewingContratoLive.id,
      dataInicio: viewingContratoLive.dataInicio,
      registros: filterRegistrosDoContrato(viewingContratoLive, snapshot.registrosHorimetro ?? []),
    };
  }, [viewingContratoLive, snapshot?.registrosHorimetro]);

  const abastecimentoContratoContext = useMemo(() => {
    if (!viewingContratoLive || abastecimentoPresetMaquinaId || !snapshot) return null;
    return {
      contrato: viewingContratoLive,
      registros: filterRegistrosDoContrato(viewingContratoLive, snapshot.registrosHorimetro ?? []),
    };
  }, [viewingContratoLive, abastecimentoPresetMaquinaId, snapshot?.registrosHorimetro]);

  const timelineMaquinaLive = useMemo(() => {
    if (!timelineMaquina || !snapshot) return null;
    return maquinasFrota.find((m) => m.id === timelineMaquina.id) ?? null;
  }, [timelineMaquina, snapshot, maquinasFrota]);

  useEffect(() => {
    if (!snapshot) return;
    setEditingMaquina((prev) => {
      if (!prev) return prev;
      return maquinasFrota.find((m) => m.id === prev.id) ?? prev;
    });
    setEditingContrato((prev) => {
      if (!prev) return prev;
      const fresh = snapshot.contratos?.find((c) => c.id === prev.id);
      if (!fresh) {
        setContratoModalOpen(false);
        return null;
      }
      return fresh;
    });
    if (viewingContrato && !snapshot.contratos?.some((c) => c.id === viewingContrato.id)) {
      setViewingContrato(null);
    }
    setEditingHorimetro((prev) => {
      if (!prev) return prev;
      const fresh = snapshot.registrosHorimetro?.find((r) => r.id === prev.id);
      if (!fresh) {
        setHorimetroModalOpen(false);
        return null;
      }
      return fresh;
    });
    setEditingAbastecimento((prev) => {
      if (!prev) return prev;
      const fresh = snapshot.abastecimentos?.find((a) => a.id === prev.id);
      if (!fresh) {
        setAbastecimentoModalOpen(false);
        return null;
      }
      return fresh;
    });
    if (timelineMaquina && !maquinasFrota.some((m) => m.id === timelineMaquina.id)) {
      setTimelineMaquina(null);
    }
  }, [snapshot, timelineMaquina, viewingContrato, maquinasFrota]);

  const openConfirm = (request: ConfirmRequest) => setConfirmRequest(request);

  const closeConfirm = () => {
    if (!confirmBusy) setConfirmRequest(null);
  };

  const runConfirm = async () => {
    if (!confirmRequest) return;
    setConfirmBusy(true);
    try {
      await confirmRequest.action();
      setConfirmRequest(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const goContratos = () => setTab("contratos");

  const openNovoContrato = (tipo: AtivoTipo = "maquinario") => {
    setEditingContrato(null);
    setContratoModalInitialTipo(tipo);
    setContratoModalOpen(true);
  };

  const handleSaveContrato = async (values: ContratoFormValues, editingId: string | null) => {
    setBusy(true);
    try {
      if (editingId) {
        const { status, justificativaAlteracao, ...rest } = values;
        await updateContrato(editingId, {
          ...rest,
          ...(status ? { status } : {}),
          justificativaAlteracao,
        });
      } else {
        await createContrato(values);
      }
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar contrato."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteContrato = async (c: ContratoDto) => {
    const shouldClearScope = scope.contratoId === c.id;
    setBusy(true);
    setError(null);
    try {
      await deleteContrato(c.id);
      if (viewingContrato?.id === c.id) setViewingContrato(null);
      if (shouldClearScope) {
        setScopeDraft(EMPTY_LOCACOES_SCOPE);
        setScope({});
        setChartsFiltersApplied(false);
        await load({ scopeOverride: {} });
      } else {
        await reloadAfterMutation();
      }
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir contrato."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteContrato = (c: ContratoDto) => {
    openConfirm({
      title: "Excluir contrato",
      message:
        `Excluir contrato de "${c.clienteSiengeNome}"?\n\n` +
        "Todos os dados vinculados a este contrato serão removidos: locações, registros de horímetro, abastecimentos, manutenções e medições do período.",
      confirmLabel: "Excluir",
      action: () => executeDeleteContrato(c),
    });
  };

  const handleDeleteContratoModal = () => {
    if (!editingContrato) return;
    const contrato = editingContrato;
    openConfirm({
      title: "Excluir contrato",
      message:
        `Excluir contrato de "${contrato.clienteSiengeNome}"?\n\n` +
        "Todos os dados vinculados a este contrato serão removidos: locações, registros de horímetro, abastecimentos, manutenções e medições do período.",
      confirmLabel: "Excluir",
      action: async () => {
        await executeDeleteContrato(contrato);
        setContratoModalOpen(false);
        setEditingContrato(null);
      },
    });
  };

  const handlePipelineMove = async (contratoId: string, fase: ContratoPipelineFase) => {
    setBusy(true);
    setError(null);
    try {
      await updateContratoPipelineFase(contratoId, fase);
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao mover contrato no pipeline."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleAprovarFila = async (item: FilaAprovacaoItem) => {
    setBusy(true);
    setError(null);
    try {
      if (item.tipo === "horimetro") await aprovarRegistroHorimetro(item.id);
      else if (item.tipo === "manutencao") await aprovarManutencao(item.id);
      else {
        const { aprovarPareamentoCarrada } = await import("@/services/carradas");
        await aprovarPareamentoCarrada(item.id);
      }
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao aprovar solicitação."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleRejeitarFila = async (item: FilaAprovacaoItem, motivo: string) => {
    setBusy(true);
    setError(null);
    try {
      if (item.tipo === "horimetro") await rejeitarRegistroHorimetro(item.id, motivo);
      else if (item.tipo === "manutencao") await rejeitarManutencao(item.id, motivo);
      else {
        const { rejeitarPareamentoCarrada } = await import("@/services/carradas");
        await rejeitarPareamentoCarrada(item.id, motivo);
      }
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao rejeitar solicitação."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveOperadores = async (operadores: ContratoOperadorDto[]) => {
    if (!viewingContratoLive) return;
    setBusy(true);
    try {
      await updateContrato(viewingContratoLive.id, { operadores });
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar operadores."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveMaquina = async (values: MaquinarioFormValues, editingId: string | null) => {
    setBusy(true);
    const tipoAtivo =
      maquinaModalLockedTipo ?? resolveAtivoTipo({ tipoAtivo: values.tipoAtivo, tipo: values.tipo });
    const payload: MaquinarioFormValues = { ...values, tipoAtivo };
    try {
      if (editingId) {
        await updateMaquinario(editingId, payload);
      } else {
        const created = await createMaquinario(payload);
        if (viewingContratoLive && !viewingContratoLive.maquinaIds.includes(created.id)) {
          await updateContrato(viewingContratoLive.id, {
            maquinaIds: [...viewingContratoLive.maquinaIds, created.id],
            justificativaAlteracao: "Máquina vinculada ao contrato via cadastro de maquinário.",
          });
        }
      }
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar maquinário."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteMaquina = async () => {
    if (!editingMaquina) return;
    setBusy(true);
    setError(null);
    const id = editingMaquina.id;
    try {
      await deleteMaquinario(id);
      setMaquinaModalOpen(false);
      setEditingMaquina(null);
      if (timelineMaquina?.id === id) setTimelineMaquina(null);
      setSnapshot((prev) =>
        prev
          ? {
              ...prev,
              maquinarios: prev.maquinarios.filter((m) => m.id !== id),
              manutencoes: prev.manutencoes.filter((m) => m.maquinaId !== id),
              registrosHorimetro: prev.registrosHorimetro.filter((r) => r.maquinaId !== id),
              abastecimentos: prev.abastecimentos.filter((a) => a.maquinaId !== id),
            }
          : prev,
      );
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir maquinário."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteMaquina = () => {
    if (!editingMaquina) return;
    const maquina = editingMaquina;
    openConfirm({
      title: "Excluir maquinário",
      message: `Excluir "${maquina.nome}"? Todos os registros vinculados (horímetro, abastecimentos, manutenções) serão removidos.`,
      confirmLabel: "Excluir",
      action: executeDeleteMaquina,
    });
  };

  const handleSaveOperador = async (values: OperadorFormValues, editingId: string | null) => {
    setBusy(true);
    try {
      if (editingId) {
        await updateOperador(editingId, values);
      } else {
        await createOperador(values);
      }
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar operador."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteOperador = async () => {
    if (!editingOperador) return;
    setBusy(true);
    setError(null);
    const id = editingOperador.id;
    try {
      await deleteOperador(id);
      setOperadorModalOpen(false);
      setEditingOperador(null);
      setSnapshot((prev) =>
        prev ? { ...prev, operadores: (prev.operadores ?? []).filter((o) => o.id !== id) } : prev,
      );
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir operador."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteOperador = () => {
    if (!editingOperador) return;
    const operador = editingOperador;
    openConfirm({
      title: "Excluir operador",
      message: `Excluir operador "${operador.nome}"?`,
      confirmLabel: "Excluir",
      action: executeDeleteOperador,
    });
  };

  const executeDeleteOperadorRow = async (op: OperadorCatalogDto) => {
    setBusy(true);
    setError(null);
    try {
      await deleteOperador(op.id);
      setSnapshot((prev) =>
        prev ? { ...prev, operadores: (prev.operadores ?? []).filter((o) => o.id !== op.id) } : prev,
      );
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir operador."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteOperadorRow = (op: OperadorCatalogDto) => {
    openConfirm({
      title: "Excluir operador",
      message: `Excluir operador "${op.nome}"?`,
      confirmLabel: "Excluir",
      action: () => executeDeleteOperadorRow(op),
    });
  };

  const handleSaveManutencao = async (values: Parameters<typeof createManutencao>[0] & { dataConclusao?: string | null }) => {
    setBusy(true);
    try {
      if (editingManutencao) {
        await updateManutencao(editingManutencao.id, {
          tipo: values.tipo,
          descricao: values.descricao,
          horimetroAtual: values.horimetroAtual,
          prioridade: values.prioridade,
          responsavelId: values.responsavelId,
          responsavelNome: values.responsavelNome,
          dataAbertura: values.dataAbertura,
          dataPrevista: values.dataPrevista || null,
          ...(values.custoEstimado !== undefined ? { custoEstimado: values.custoEstimado } : {}),
          ...(values.dataConclusao !== undefined ? { dataConclusao: values.dataConclusao } : {}),
        });
      } else if (manutencaoGaragemMode) {
        await registrarManutencaoGaragem(values);
      } else {
        await createManutencao(values);
      }
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar manutenção."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteManutencao = async () => {
    if (!editingManutencao) return;
    setBusy(true);
    try {
      await deleteManutencao(editingManutencao.id);
      setManutencaoModalOpen(false);
      setEditingManutencao(null);
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir manutenção."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteManutencao = () => {
    if (!editingManutencao) return;
    const manutencao = editingManutencao;
    openConfirm({
      title: "Excluir manutenção",
      message: `Excluir manutenção de "${manutencao.maquinaNome}"?`,
      confirmLabel: "Excluir",
      action: executeDeleteManutencao,
    });
  };

  const handleDeleteManutencaoRow = (m: ManutencaoDto) => {
    openConfirm({
      title: "Excluir manutenção",
      message: `Excluir manutenção de "${m.maquinaNome}"?`,
      confirmLabel: "Excluir",
      action: async () => {
        setBusy(true);
        try {
          await deleteManutencao(m.id);
          await reloadAfterMutation();
        } catch (e) {
          setError(extractApiError(e, "Erro ao excluir manutenção."));
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleUpdateManutencaoStatus = async (id: string, status: ManutencaoStatus) => {
    setBusy(true);
    try {
      if (status === "finalizada") await concluirManutencao(id);
      else await updateManutencaoStatus(id, status);
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao atualizar manutenção."));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveHorimetro = async (values: Parameters<typeof createRegistroHorimetro>[0]) => {
    setBusy(true);
    try {
      let registro: RegistroHorimetroDto;
      if (editingHorimetro) {
        registro = await updateRegistroHorimetro(editingHorimetro.id, {
          tipo: values.tipo,
          horimetroInformado: values.horimetroInformado,
          observacao: values.observacao,
          dataHora: values.dataHora,
        });
      } else {
        registro = await createRegistroHorimetro({
          ...values,
          ...(viewingContratoLive ? { contratoId: viewingContratoLive.id } : {}),
        });
      }

      setSnapshot((prev) => {
        if (!prev) return prev;
        const registrosHorimetro = editingHorimetro
          ? prev.registrosHorimetro.map((r) => (r.id === registro.id ? registro : r))
          : [registro, ...prev.registrosHorimetro];
        const maquinarios = prev.maquinarios.map((m) =>
          m.id === registro.maquinaId
            ? { ...m, horimetroAtual: Math.max(m.horimetroAtual, registro.horimetroInformado) }
            : m,
        );
        return { ...prev, registrosHorimetro, maquinarios };
      });

      return registro;
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar registro de horímetro."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteHorimetro = async () => {
    if (!editingHorimetro) return;
    setBusy(true);
    setError(null);
    const id = editingHorimetro.id;
    try {
      await deleteRegistroHorimetro(id);
      setHorimetroModalOpen(false);
      setEditingHorimetro(null);
      setSnapshot((prev) =>
        prev
          ? { ...prev, registrosHorimetro: prev.registrosHorimetro.filter((x) => x.id !== id) }
          : prev,
      );
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir registro de horímetro."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteHorimetro = () => {
    if (!editingHorimetro) return;
    const registro = editingHorimetro;
    openConfirm({
      title: "Excluir registro de horímetro",
      message: `Excluir registro de horímetro de "${registro.maquinaNome}"?`,
      confirmLabel: "Excluir",
      action: executeDeleteHorimetro,
    });
  };

  const handleSaveAbastecimento = async (values: Parameters<typeof createAbastecimento>[0]) => {
    setBusy(true);
    try {
      if (editingAbastecimento) {
        return await updateAbastecimento(editingAbastecimento.id, {
          data: values.data,
          horimetroAtual: values.horimetroAtual,
          litros: values.litros,
          valorTotal: values.valorTotal,
          tipoCombustivel: values.tipoCombustivel,
          observacao: values.observacao,
        });
      }
      return await createAbastecimento({
        ...values,
        ...(viewingContratoLive ? { contratoId: viewingContratoLive.id } : {}),
      });
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar abastecimento."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const executeDeleteAbastecimento = async () => {
    if (!editingAbastecimento) return;
    setBusy(true);
    try {
      await deleteAbastecimento(editingAbastecimento.id);
      setAbastecimentoModalOpen(false);
      setEditingAbastecimento(null);
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir abastecimento."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAbastecimento = () => {
    if (!editingAbastecimento) return;
    const abastecimento = editingAbastecimento;
    openConfirm({
      title: "Excluir abastecimento",
      message: `Excluir abastecimento de "${abastecimento.maquinaNome}"?`,
      confirmLabel: "Excluir",
      action: executeDeleteAbastecimento,
    });
  };

  const executeDeleteHorimetroRow = async (r: RegistroHorimetroDto) => {
    setBusy(true);
    setError(null);
    try {
      await deleteRegistroHorimetro(r.id);
      setSnapshot((prev) =>
        prev
          ? { ...prev, registrosHorimetro: prev.registrosHorimetro.filter((x) => x.id !== r.id) }
          : prev,
      );
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir registro de horímetro."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteHorimetroRow = (r: RegistroHorimetroDto) => {
    openConfirm({
      title: "Excluir registro de horímetro",
      message: `Excluir registro de "${r.maquinaNome}"?`,
      confirmLabel: "Excluir",
      action: () => executeDeleteHorimetroRow(r),
    });
  };

  const executeDeleteAbastecimentoRow = async (a: AbastecimentoDto) => {
    setBusy(true);
    setError(null);
    try {
      await deleteAbastecimento(a.id);
      setSnapshot((prev) =>
        prev ? { ...prev, abastecimentos: prev.abastecimentos.filter((x) => x.id !== a.id) } : prev,
      );
      await reloadAfterMutation();
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir abastecimento."));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAbastecimentoRow = (a: AbastecimentoDto) => {
    openConfirm({
      title: "Excluir abastecimento",
      message: `Excluir abastecimento de "${a.maquinaNome}"?`,
      confirmLabel: "Excluir",
      action: () => executeDeleteAbastecimentoRow(a),
    });
  };

  const openHorimetroModal = () => {
    setEditingHorimetro(null);
    setHorimetroModalOpen(true);
  };

  const openHorimetroEdit = (r: RegistroHorimetroDto) => {
    setEditingHorimetro(r);
    setHorimetroModalOpen(true);
  };

  const handleConferirFotosFila = (item: FilaAprovacaoItem) => {
    const registro = snapshot?.registrosHorimetro?.find((r) => r.id === item.id);
    if (!registro) return;
    if (item.contratoId) {
      const contrato = snapshot?.contratos?.find((c) => c.id === item.contratoId);
      if (contrato) {
        setTab("contratos");
        openContratoView(contrato);
      }
    }
    openHorimetroEdit(registro);
  };

  const openAbastecimentoModal = (presetMaquinaId?: string) => {
    if (viewingContratoLive && isCombustivelPagoCliente(viewingContratoLive)) {
      setError("Este contrato tem combustível pago pelo cliente — abastecimentos não são registrados aqui.");
      return;
    }
    setEditingAbastecimento(null);
    setAbastecimentoPresetMaquinaId(typeof presetMaquinaId === "string" && presetMaquinaId ? presetMaquinaId : null);
    setAbastecimentoModalOpen(true);
  };

  const openManutencaoGaragem = (maquinaId: string) => {
    setEditingManutencao(null);
    setManutencaoGaragemMode(true);
    setGaragemMaquinaId(maquinaId);
    setManutencaoModalOpen(true);
  };

  const openManutencaoModal = () => {
    setEditingManutencao(null);
    setManutencaoGaragemMode(false);
    setManutencaoModalOpen(true);
  };

  const openNovaMaquina = () => {
    setEditingMaquina(null);
    setPrefillMaquina(null);
    setMaquinaModalLockedTipo("maquinario");
    setMaquinaModalOpen(true);
  };

  const openNovoEquipamento = () => {
    setEditingMaquina(null);
    setPrefillMaquina(null);
    setMaquinaModalLockedTipo("equipamento");
    setMaquinaModalOpen(true);
  };

  const openEditAtivo = (m: MaquinarioDto) => {
    setEditingMaquina(m);
    setPrefillMaquina(null);
    setMaquinaModalLockedTipo(resolveAtivoTipo(m));
    setMaquinaModalOpen(true);
  };

  const openDuplicateAtivo = (m: MaquinarioDto) => {
    setEditingMaquina(null);
    setPrefillMaquina(m);
    setMaquinaModalLockedTipo(resolveAtivoTipo(m));
    setMaquinaModalOpen(true);
  };

  const openNovoOperador = () => {
    setEditingOperador(null);
    setOperadorModalOpen(true);
  };

  const openEncerrarLocacao = (locacao: LocacaoDto) => {
    setEncerrarLocacaoTarget(locacao);
    setEncerrarLocacaoOpen(true);
  };

  const handleEncerrarLocacao = async (values: {
    horimetroRetorno: number;
    odometroRetorno: number;
    dataFimReal: string;
  }) => {
    if (!encerrarLocacaoTarget) return;
    setBusy(true);
    try {
      await encerrarLocacao(encerrarLocacaoTarget.id, values);
    } catch (e) {
      setError(extractApiError(e, "Erro ao encerrar locação."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const openCancelarLocacao = (locacao: LocacaoDto) => {
    setCancelLocacaoTarget(locacao);
    setCancelLocacaoOpen(true);
  };

  const handleConfirmCancelarLocacao = async (motivo: string) => {
    if (!cancelLocacaoTarget) return;
    setBusy(true);
    setError(null);
    try {
      await cancelarLocacao(cancelLocacaoTarget.id, motivo || undefined);
    } catch (e) {
      setError(extractApiError(e, "Erro ao cancelar locação."));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const locacoesAtivas = useMemo(() => {
    const ativas = (snapshot?.locacoes ?? []).filter((l) => l.status === "ativa");
    if (filtroTipo === "todos") return ativas;
    const byId = new Map(maquinasFrota.map((m) => [m.id, m]));
    return ativas.filter((l) => resolveAtivoTipo(byId.get(l.maquinaId) ?? { tipoAtivo: "maquinario", tipo: "" }) === filtroTipo);
  }, [snapshot, filtroTipo, maquinasFrota]);

  const operadorMode = snapshot?.accessMode === "operador";

  const garagemMaquinaLive = useMemo(() => {
    if (!garagemMaquinaId || !snapshot) return null;
    return maquinasFrota.find((m) => m.id === garagemMaquinaId) ?? null;
  }, [garagemMaquinaId, snapshot, maquinasFrota]);

  const visibleTabs = useMemo(
    () =>
      operadorMode
        ? TABS.filter((t) => t.id !== "resumo" && t.id !== "garagem" && t.id !== "carradas")
        : TABS,
    [operadorMode],
  );

  useEffect(() => {
    if (operadorMode && (isCadastroTab(tab) || tab === "resumo" || tab === "garagem" || tab === "carradas")) {
      setTab("contratos");
    }
  }, [operadorMode, tab]);

  const selectTab = useCallback((id: TabId) => {
    setTab(id);
    setError(null);
    if (id === "resumo" || id === "dashboard" || isCadastroTab(id) || id === "garagem" || id === "carradas") {
      setViewingContrato(null);
    }
    if (id !== "garagem") {
      setGaragemMaquinaId(null);
    }
  }, []);

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden bg-slate-50">
      <div className="no-print relative z-20 flex flex-shrink-0 flex-wrap gap-2 border-b border-slate-200 bg-white px-6 py-2.5">
        {visibleTabs.flatMap(({ id, label, icon: Icon }) => {
          const tabButton = (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={locacoesTabClass(tab === id)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );

          if (id === "dashboard" && !operadorMode) {
            return [
              tabButton,
              <CadastrosTabMenu
                key="cadastros-menu"
                active={isCadastroTab(tab)}
                currentTab={isCadastroTab(tab) ? tab : null}
                onSelect={selectTab}
              />,
            ];
          }

          return [tabButton];
        })}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex w-full flex-col gap-6 p-6">
            <div className="no-print flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-slate-800">Semog Locações</h1>
                <p className="mt-0.5 text-sm text-slate-500">
                  {tab === "resumo"
                    ? "Resumo das máquinas da frota no período filtrado"
                    : operadorMode
                      ? "Registre e confira horímetros e fotos dos seus contratos"
                      : "Contratos, horímetro, abastecimentos e operação das máquinas"}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RefreshCw className="h-4 w-4" />}
                onClick={() => void load()}
                loading={loading}
              >
                Atualizar
              </Button>
            </div>

            {tab === "dashboard" && (
              <LocacoesFilterBar
                period={dashboardPeriodDraft}
                onPeriodChange={setDashboardPeriodDraft}
                filtroTipo={filtroTipoDraft}
                onFiltroTipoChange={handleFiltroTipoDraftChange}
                showScope
                contratos={contratosParaEscopo}
                maquinarios={maquinasParaEscopo}
                scope={scopeDraft}
                onScopeChange={handleScopeDraftChange}
                onApply={applyFilters}
                onClearScope={clearScope}
                showClearScope={hasScopeDraft || chartsFiltersApplied}
                loading={loading}
                showArquivados={!operadorMode}
                onArquivados={() => setIsArquivadosModalOpen(true)}
              />
            )}

            {error && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
                  Tentar novamente
                </Button>
              </div>
            )}

        {loading && !snapshot ? (
          <div className="flex justify-center py-16">
            <Loader size="lg" label="Carregando locações..." />
          </div>
        ) : snapshot ? (
          <>
            {tab === "resumo" && (
                <LocacoesResumoDashboard
                  period={resumoPeriod}
                  onPeriodChange={setResumoPeriod}
                  metrics={snapshot.metrics ?? emptyMetrics()}
                  deltas={snapshot.deltas ?? EMPTY_DELTAS}
                  maquinarios={snapshot.maquinarios ?? []}
                  locacoes={snapshot.locacoes ?? []}
                  contratos={snapshot.contratos ?? []}
                  registrosHorimetro={snapshot.registrosHorimetro ?? []}
                  filters={resumoFilters}
                  onFiltersChange={setResumoFilters}
                  ready={resumoSnapshotMatches(snapshot, resumoPeriod, resumoFilters)}
                  loading={loading}
                />
              )}

            {tab === "cadastro-maquinas" && (
              <TabCadastroMaquinas
                maquinarios={maquinasCadastro}
                onNovaMaquina={openNovaMaquina}
                onEditMaquina={openEditAtivo}
                onDuplicateMaquina={openDuplicateAtivo}
              />
            )}

            {tab === "cadastro-equipamentos" && (
              <TabCadastroEquipamentos
                equipamentos={equipamentosCadastro}
                onNovoEquipamento={openNovoEquipamento}
                onEditEquipamento={openEditAtivo}
                onDuplicateEquipamento={openDuplicateAtivo}
              />
            )}

            {tab === "cadastro-operadores" && (
              <TabCadastroOperadores
                operadores={snapshot.operadores ?? []}
                busy={busy}
                onNovoOperador={openNovoOperador}
                onEditOperador={(o) => {
                  setEditingOperador(o);
                  setOperadorModalOpen(true);
                }}
                onDeleteOperador={(o) => void handleDeleteOperadorRow(o)}
              />
            )}

            {tab === "dashboard" && (
              <LocacoesDashboard
                dashboard={snapshot.dashboard}
                metrics={metrics}
                deltas={deltas}
                biSummary={snapshot.biSummary}
                filaAprovacao={snapshot.filaAprovacao ?? []}
                canApproveLocacoes={snapshot.canApproveLocacoes ?? false}
                approvalBusy={busy}
                period={dashboardPeriod}
                showCharts={showDashboardCharts}
                chartsLoading={loading && chartsFiltersApplied}
                scopeLabel={
                  scope.clienteSiengeId || scope.contratoId || scope.maquinaId ? scopeLabel : null
                }
                scopeContratoId={scope.contratoId}
                scopeMaquinaId={scope.maquinaId}
                maquinarios={maquinasDashboard}
                contratosAtivos={contratosAtivos}
                contratosFiltro={contratosFiltrados}
                manutencoesAbertas={manutencoesAbertas}
                registrosHorimetro={snapshot.registrosHorimetro ?? []}
                abastecimentos={snapshot.abastecimentos ?? []}
                onAprovarFila={operadorMode ? undefined : handleAprovarFila}
                onRejeitarFila={operadorMode ? undefined : handleRejeitarFila}
                onConferirFotosFila={operadorMode ? undefined : handleConferirFotosFila}
                operadorMode={operadorMode}
                ativoModo={filtroTipo}
                onNovaMaquina={
                  operadorMode
                    ? undefined
                    : filtroTipo === "equipamento"
                      ? openNovoEquipamento
                      : openNovaMaquina
                }
                locacoesAtivas={locacoesAtivas}
                onEncerrarLocacao={openEncerrarLocacao}
                onCancelarLocacao={openCancelarLocacao}
                onEditMaquina={
                  operadorMode
                    ? undefined
                    : (m) => openEditAtivo(m)
                }
                onEditHorimetro={(r) => openHorimetroEdit(r)}
                onDeleteHorimetro={operadorMode ? undefined : (r) => void handleDeleteHorimetroRow(r)}
                onEditAbastecimento={
                  operadorMode
                    ? undefined
                    : (a) => {
                        setAbastecimentoPresetMaquinaId(null);
                        setEditingAbastecimento(a);
                        setAbastecimentoModalOpen(true);
                      }
                }
                onDeleteAbastecimento={operadorMode ? undefined : (a) => void handleDeleteAbastecimentoRow(a)}
                onEditContrato={
                  isAdmin && !operadorMode
                    ? (c) => {
                        goContratos();
                        setEditingContrato(c);
                        setContratoModalOpen(true);
                      }
                    : undefined
                }
              />
            )}

            {tab === "garagem" && !operadorMode && (
              garagemMaquinaLive ? (
                <GaragemMaquinaDetail
                  maquina={garagemMaquinaLive}
                  locacoes={snapshot.locacoes ?? []}
                  abastecimentos={snapshot.abastecimentos ?? []}
                  manutencoes={snapshot.manutencoes ?? []}
                  onVoltar={() => setGaragemMaquinaId(null)}
                  onNovoAbastecimento={() => openAbastecimentoModal(garagemMaquinaLive.id)}
                  onRegistrarManutencao={() => openManutencaoGaragem(garagemMaquinaLive.id)}
                  onEditAbastecimento={(a) => {
                    setEditingAbastecimento(a);
                    setAbastecimentoPresetMaquinaId(garagemMaquinaLive.id);
                    setAbastecimentoModalOpen(true);
                  }}
                  onDeleteAbastecimento={(a) => void handleDeleteAbastecimentoRow(a)}
                />
              ) : (
                <TabGaragem
                  maquinarios={maquinasFrotaFiltradas}
                  contratos={snapshot.contratos ?? []}
                  locacoes={snapshot.locacoes ?? []}
                  manutencoes={snapshot.manutencoes ?? []}
                  onVerMaquina={(m) => setGaragemMaquinaId(m.id)}
                  onNovoAbastecimento={(m) => openAbastecimentoModal(m.id)}
                  onRegistrarManutencao={(m) => openManutencaoGaragem(m.id)}
                />
              )
            )}

            {tab === "carradas" && !operadorMode && <TabCarradas />}

            {tab === "contratos" &&
              (viewingContratoLive ? (
                <ContratoDetailView
                  contrato={viewingContratoLive}
                  snapshot={snapshot}
                  busy={busy}
                  onBack={() => setViewingContrato(null)}
                  onEditContrato={() => {
                    setEditingContrato(viewingContratoLive);
                    setContratoModalOpen(true);
                  }}
                  onRefreshSnapshot={reloadAfterMutation}
                  onSaveOperadores={handleSaveOperadores}
                  onNovoHorimetro={() => openHorimetroModal()}
                  onEditHorimetro={(r) => openHorimetroEdit(r)}
                  onDeleteHorimetro={operadorMode ? undefined : (r) => void handleDeleteHorimetroRow(r)}
                  onNovoAbastecimento={operadorMode ? undefined : () => openAbastecimentoModal()}
                  onEditAbastecimento={
                    operadorMode
                      ? undefined
                      : (a) => {
                          setAbastecimentoPresetMaquinaId(null);
                          setEditingAbastecimento(a);
                          setAbastecimentoModalOpen(true);
                        }
                  }
                  onDeleteAbastecimento={operadorMode ? undefined : (a) => void handleDeleteAbastecimentoRow(a)}
                  onEditMaquina={operadorMode ? undefined : openEditAtivo}
                  onDuplicateMaquina={operadorMode ? undefined : openDuplicateAtivo}
                  onNovaMaquina={operadorMode ? undefined : openNovaMaquina}
                  onHistoricoMaquina={operadorMode ? undefined : setTimelineMaquina}
                  onNovaManutencao={operadorMode ? undefined : () => openManutencaoModal()}
                  onEditManutencao={
                    operadorMode
                      ? undefined
                      : (m) => {
                          setEditingManutencao(m);
                          setManutencaoGaragemMode(false);
                          setManutencaoModalOpen(true);
                        }
                  }
                  onDeleteManutencao={operadorMode ? undefined : (m) => void handleDeleteManutencaoRow(m)}
                  onUpdateManutencaoStatus={(id, status) => void handleUpdateManutencaoStatus(id, status)}
                  operadorMode={operadorMode}
                  operadoresCatalog={snapshot.operadores ?? []}
                />
              ) : (
                <TabContratos
                  contratos={contratosFiltrados}
                  pipelineCards={operadorMode ? [] : (snapshot.pipelineCards ?? [])}
                  maquinarios={maquinasFrotaFiltradas}
                  busy={busy}
                  onNovo={operadorMode ? undefined : openNovoContrato}
                  onEdit={
                    isAdmin && !operadorMode
                      ? (c) => {
                          setEditingContrato(c);
                          setContratoModalOpen(true);
                        }
                      : undefined
                  }
                  onDelete={operadorMode ? undefined : (c) => void handleDeleteContrato(c)}
                  onView={openContratoView}
                  onPipelineMove={operadorMode ? undefined : handlePipelineMove}
                />
              ))}
          </>
        ) : null}
          </div>
        </div>
      </div>

      <ModalContrato
        open={contratoModalOpen}
        editing={editingContrato}
        initialTipoContrato={contratoModalInitialTipo}
        maquinarios={maquinasFrota}
        contratos={snapshot?.contratos ?? []}
        busy={busy}
        onClose={() => {
          setContratoModalOpen(false);
          setEditingContrato(null);
        }}
        onSave={handleSaveContrato}
        onDelete={editingContrato ? () => void handleDeleteContratoModal() : undefined}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalMaquinario
        open={maquinaModalOpen}
        editing={editingMaquina}
        prefill={prefillMaquina}
        lockedTipoAtivo={maquinaModalLockedTipo}
        busy={busy}
        onClose={() => {
          setMaquinaModalOpen(false);
          setEditingMaquina(null);
          setPrefillMaquina(null);
          setMaquinaModalLockedTipo(undefined);
        }}
        onSave={handleSaveMaquina}
        onDelete={editingMaquina ? () => void handleDeleteMaquina() : undefined}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalOperador
        open={operadorModalOpen}
        editing={editingOperador}
        busy={busy}
        onClose={() => {
          setOperadorModalOpen(false);
          setEditingOperador(null);
        }}
        onSave={handleSaveOperador}
        onDelete={editingOperador ? () => void handleDeleteOperador() : undefined}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalManutencao
        open={manutencaoModalOpen}
        editing={editingManutencao}
        maquinarios={manutencaoGaragemMode && garagemMaquinaId ? maquinasFrota.filter((m) => m.id === garagemMaquinaId) : maquinasContexto}
        usuarios={usuarios}
        presetMaquinaId={editingManutencao ? null : manutencaoGaragemMode ? garagemMaquinaId : null}
        busy={busy}
        onClose={() => {
          setManutencaoModalOpen(false);
          setManutencaoGaragemMode(false);
          setEditingManutencao(null);
        }}
        onSave={handleSaveManutencao}
        onDelete={editingManutencao ? () => void handleDeleteManutencao() : undefined}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalRegistroHorimetro
        open={horimetroModalOpen}
        editing={editingHorimetro}
        maquinarios={maquinasContexto}
        accessMode={snapshot?.accessMode}
        contratoContext={horimetroContratoContext}
        busy={busy}
        onClose={() => {
          setHorimetroModalOpen(false);
          setEditingHorimetro(null);
        }}
        onSave={handleSaveHorimetro}
        onDelete={editingHorimetro ? () => void handleDeleteHorimetro() : undefined}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalAbastecimento
        open={abastecimentoModalOpen}
        editing={editingAbastecimento}
        maquinarios={maquinariosAbastecimentoModal}
        contratoContext={abastecimentoContratoContext}
        presetMaquinaId={editingAbastecimento ? null : abastecimentoPresetMaquinaId}
        busy={busy}
        onClose={() => {
          setAbastecimentoModalOpen(false);
          setEditingAbastecimento(null);
          setAbastecimentoPresetMaquinaId(null);
        }}
        onSave={handleSaveAbastecimento}
        onDelete={editingAbastecimento ? () => void handleDeleteAbastecimento() : undefined}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalEncerrarLocacao
        open={encerrarLocacaoOpen}
        locacao={encerrarLocacaoTarget}
        busy={busy}
        onClose={() => {
          setEncerrarLocacaoOpen(false);
          setEncerrarLocacaoTarget(null);
        }}
        onConfirm={handleEncerrarLocacao}
        onMutationComplete={reloadAfterMutation}
      />

      <ModalCancelarLocacao
        open={cancelLocacaoOpen}
        locacao={cancelLocacaoTarget}
        busy={busy}
        onClose={() => {
          setCancelLocacaoOpen(false);
          setCancelLocacaoTarget(null);
        }}
        onConfirm={handleConfirmCancelarLocacao}
        onMutationComplete={reloadAfterMutation}
      />

      {timelineMaquinaLive && (
        <MaquinaHistoricoModal open maquinario={timelineMaquinaLive} onClose={() => setTimelineMaquina(null)} />
      )}

      <ModalContratosArquivados
        open={isArquivadosModalOpen}
        onClose={() => setIsArquivadosModalOpen(false)}
        maquinarios={maquinasFrota}
      />

      <ConfirmModal
        isOpen={confirmRequest != null}
        title={confirmRequest?.title ?? ""}
        message={confirmRequest?.message ?? ""}
        confirmLabel={confirmRequest?.confirmLabel}
        onConfirm={runConfirm}
        onCancel={closeConfirm}
        loading={confirmBusy || busy}
      />
    </div>
  );
}
